import { useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { useCleanup } from 'utils'
import Btn from './Btn'

const STATUS = Object.freeze({ READY: 0, SAVING: 1, SAVED: 2 })

/**
 * @param texts {string[]} List of texts for the three statuses: initial, saving, saved. If only two are given, the
 * button will not show the Saved status. If the action returns boolean false, Saved is also skipped.
 * @param icon {null|string} Which icon to show on the initial state (set NULL for no icon)
 * @param iconSize {string} Defines the icon size, if needed
 * @param action {Function} An ASYNC function that receives the onClick event. May return boolean false to skip the
 * Saved state.
 * @param props
 * @todo Won't work as a Dropdown.Item! See https://github.com/react-bootstrap/react-bootstrap/issues/5082
 */
export default function ActBtn({ texts, iconSize, action, icon = null, ...props }) {
	let [state, setter] = useState(STATUS.READY)

	//region// unmount properly, without leaking the local state after the button is gone
	//This might happen when the user is in a rush and leaves the screen before the final transitions, or when the ActBtn
	// causes a screen change on itself (such as login/logout)
	const resetTimeout = useRef(0)
	const goneAlready  = useRef(false)

	//overwrite the actual setter to make it a no-op in case the button is already gone
	const origSetter = setter
	setter = (v) => {
		if (!goneAlready.current) {
			origSetter(v)
		}
	}

	useCleanup(() => {
		if (resetTimeout.current) {
			window.clearTimeout(resetTimeout.current)
		}
		goneAlready.current = true
	}, [resetTimeout])
	//endregion

	const onClick = async (event) => {
		setter(STATUS.SAVING)
		try {
			const result = await action(event)
			if (result === false) {
				setter(STATUS.READY)
			} else if (texts.length == 3) {
				setter(STATUS.SAVED)
			resetTimeout.current = window.setTimeout(() => setter(STATUS.READY), 3000)
			} else {
				setter(STATUS.READY)
			}
		} catch (error) {
			console.error('Error during ActBtn action -', error)
			setter(STATUS.READY) //so they can... pray and try again?
		}
	}

	const iconProps = {
		icon      : {
			[STATUS.READY] : icon,
			[STATUS.SAVING]: 'spinner',
			[STATUS.SAVED] : 'check',
		}[state],
		iconSize,
		fixedWidth: true,
		pulse     : state == STATUS.SAVING,
	}

	return <Btn onClick={onClick} {...props} {...iconProps}
	            disabled={'disabled' in props? (props.disabled || state != STATUS.READY) : state != STATUS.READY}
	            noSlash={state != STATUS.READY}
	            children={texts[state]}/>
}

ActBtn.propTypes = {
	action  : PropTypes.func.isRequired,
	texts   : PropTypes.arrayOf(PropTypes.string).isRequired,
	icon    : PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
	iconSize: PropTypes.string,
}
